/**
 * @fileoverview Pre-Confirmation page
 * This page collects additional user information before account confirmation.
 *
 * @module PreConfirmation
 */
import { useState, useEffect } from "react"
import { Col, Row, message, Form, Spin, Upload, Button } from "antd"
import { UploadOutlined, FilePdfOutlined } from "@ant-design/icons"
import { useNavigate } from "react-router-dom"
import samu from "../assets/images/samu.webp"
import "../assets/styles/LoginPage.css"
import "../assets/styles/PreConfirmacion.css"
import { useTranslation } from "react-i18next"
import { createVeterinary } from "../services/VeterinaryServices"
import { chooseVeterinary } from "../services/AuthServices"
import { getMyUser } from "../services/AuthServices"
import { useAuth } from "../contexts/authContext"
import FloatingInput from "../components/FloatingInput"
import ChooseVetModal from "../components/ChooseVetModal"
import { fileToBase64 } from "../utils/filesBase64/fileToBase64"

/**
 * PreConfirmation page that collects additional user information
 *
 * @returns {JSX.Element} PreConfirmation page
 */
const PreConfirmation = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [logo, setLogo] = useState(null)
  const [pdfReport, setPdfReport] = useState(null)
  const [loading, setLoading] = useState(false)
  const [checkingVeterinary, setCheckingVeterinary] = useState(true)
  const { getToken } = useAuth()

  // New state for veterinary list modal
  const [modalVisible, setModalVisible] = useState(false)
  const [veterinaries, setVeterinaries] = useState([])

  /**
   * Check if user already has a veterinary clinic on component mount
   */
  useEffect(() => {
    const checkVeterinaryStatus = async () => {
      try {
        setCheckingVeterinary(true)
        const token = await getToken()

        if (!token) {
          navigate("/login")
          return
        }

        const userData = await getMyUser(token)

        if (userData && userData.veterinary) {
          if (!userData.active) {
            message.info(t("preConfirmation.waitingForApproval"))
            navigate("/espera")
          } else {
            message.info(t("preConfirmation.alreadyConfirmed"))
            navigate("/postconfirmacion")
          }
        }
      } catch (error) {
        console.error("Error checking veterinary status:", error)
      } finally {
        setCheckingVeterinary(false)
      }
    }

    checkVeterinaryStatus()
  }, [getToken, navigate, t])

  /**
   * Handle joining a veterinary
   */
  const handleJoinVeterinary = async (veterinaryId) => {
    try {
      const token = await getToken()
      if (!token) {
        navigate("/login")
        return
      }

      // Use the chooseVeterinary service
      await chooseVeterinary(token, veterinaryId)

      setModalVisible(false)
      message.success(t("veterinaryList.joinSuccess") || "Successfully joined veterinary clinic")
      navigate("/espera")
    } catch (error) {
      console.error("Error joining veterinary:", error)
      throw error
    }
  }

  /**
   * Handles the continue button click, calling createVeterinary
   */
  const handleContinue = async () => {
    try {
      await form.validateFields()

      const values = form.getFieldsValue()
      const { name, address, phone_number } = values

      setLoading(true)

      const token = await getToken()
      if (!token) {
        navigate("/login")
        return
      }

      const response = await createVeterinary(token, name, address, phone_number, logo, pdfReport)

      if (response && response.status === 202) {
        setVeterinaries(response.content)
        setModalVisible(true)
      } else {
        message.success(t("preConfirmation.success"))
        navigate("/espera")
      }
    } catch (error) {
      if (error.errorFields) {
        return
      }

      console.error("Error creating veterinary:", error)

      if (error.response?.status === 422) {
        message.error(t("preConfirmation.validationError"))
      } else {
        message.error(t("preConfirmation.error"))
      }
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handles logo file upload
   * @param {Object} info - Upload info object
   */
  const handleLogoUpload = async (info) => {
    if (info.file.status === "uploading") {
      return
    }

    if (info.file.status === "done") {
      const file = info.file.originFileObj

      if (file.size > 2 * 1024 * 1024) {
        message.error(t("preConfirmation.imageTooLarge"))
        return
      }

      try {
        const base64Logo = await fileToBase64(file)
        setLogo(base64Logo)
        message.success(`${file.name} uploaded successfully`)
      } catch (error) {
        console.error("Error processing logo:", error)
        message.error(t("preConfirmation.imageProcessError"))
      }
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} upload failed.`)
    }
  }

  /**
   * Handles PDF report file upload
   * @param {Object} info - Upload info object
   */
  const handlePdfUpload = async (info) => {
    if (info.file.status === "uploading") {
      return
    }

    if (info.file.status === "done") {
      const file = info.file.originFileObj

      if (file.size > 5 * 1024 * 1024) {
        message.error(t("preConfirmation.pdfTooLarge"))
        return
      }

      try {
        const base64Pdf = await fileToBase64(file)
        setPdfReport(base64Pdf)
        message.success(`${file.name} uploaded successfully`)
      } catch (error) {
        console.error("Error processing PDF:", error)
        message.error(t("preConfirmation.pdfProcessError"))
      }
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} upload failed.`)
    }
  }

  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok")
    }, 0)
  }

  return (
    <div className="body">
      {checkingVeterinary ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <Row className="content">
          <Col lg={10} sm={24} xs={24} className="newleftside2">
            <div>
              <img src={samu || "/placeholder.svg"} alt="ovejero" className="dogimage2" />
            </div>
          </Col>
          <Col lg={14} sm={24} xs={24} className="rightside">
            <Col span={24}>
              <Col span={24} className="rightside-container7">
                <div className="container">
                  <p className="bluetitle decrease left">{t("preConfirmation.welcome")}</p>
                  <p className="subtitle3">{t("preConfirmation.needMoreData")}</p>

                  <Form form={form} layout="vertical" className="veterinary-form">
                    <Form.Item
                      name="name"
                      rules={[
                        { required: true, message: t("preConfirmation.nameRequired") },
                        { min: 1, max: 100, message: t("preConfirmation.nameLength") },
                      ]}
                    >
                      <FloatingInput label={t("preConfirmation.legalName")} required={true} />
                    </Form.Item>

                    <Form.Item
                      name="address"
                      rules={[
                        { required: true, message: t("preConfirmation.addressRequired") },
                        { min: 1, max: 200, message: t("preConfirmation.addressLength") },
                      ]}
                    >
                      <FloatingInput label={t("preConfirmation.address")} required={true} />
                    </Form.Item>

                    <Form.Item
                      name="phone_number"
                      className="form-item-spacing2"
                      rules={[
                        { required: true, message: t("preConfirmation.phoneRequired") },
                        { min: 5, message: t("preConfirmation.phoneMinLength") },
                        { max: 20, message: t("preConfirmation.phoneMaxLength") },
                      ]}
                    >
                      <FloatingInput label={t("preConfirmation.phoneNumber")} required={true} />
                    </Form.Item>

                    <Form.Item
                      label={t("preConfirmation.logoOptional") || "Logo"}
                      className="form-item-spacing"
                      rules={[
                        { required: true, message: t("preConfirmation.logoRequired") },
                      ]}
                    >
                      <Upload
                        name="logo"
                        listType="picture"
                        className="logo-uploader"
                        showUploadList={true}
                        beforeUpload={(file) => {
                          const isImage = file.type.startsWith("image/")
                          if (!isImage) {
                            message.error(t("preConfirmation.imageOnly"))
                          }
                          return isImage || Upload.LIST_IGNORE
                        }}
                        customRequest={customRequest}
                        onChange={handleLogoUpload}
                        maxCount={1}
                      >
                        <Button icon={<UploadOutlined />}>{t("preConfirmation.uploadLogo") || "Upload Logo"}</Button>
                      </Upload>
                    </Form.Item>

                    <Form.Item
                      label={t("preConfirmation.pdfReport") || "PDF Report (Optional)"}
                      className="form-item-spacing"
                    >
                      <Upload
                        name="pdf"
                        listType="text"
                        className="pdf-uploader"
                        showUploadList={true}
                        beforeUpload={(file) => {
                          const isPdf = file.type === "application/pdf"
                          if (!isPdf) {
                            message.error(t("preConfirmation.pdfOnly"))
                          }
                          return isPdf || Upload.LIST_IGNORE
                        }}
                        customRequest={customRequest}
                        onChange={handlePdfUpload}
                        maxCount={1}
                      >
                        <Button icon={<FilePdfOutlined />}>{t("preConfirmation.uploadPdf") || "Upload PDF"}</Button>
                      </Upload>
                    </Form.Item>

                    <Form.Item className="button-container">
                      <button className="button9" onClick={handleContinue} disabled={loading} type="button">
                        {loading ? (
                          <span>
                            <Spin size="small" style={{ marginRight: "8px" }} /> {t("preConfirmation.processing")}
                          </span>
                        ) : (
                          t("preConfirmation.continue")
                        )}
                      </button>
                    </Form.Item>
                  </Form>
                </div>
              </Col>
            </Col>
          </Col>
        </Row>
      )}

      {/* Veterinary List Modal */}
      <ChooseVetModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        veterinaries={veterinaries}
        onJoin={handleJoinVeterinary}
      />
    </div>
  )
}

export default PreConfirmation
